<svelte:options
	customElement={{
		tag: 'rf-marker',
		shadow: 'open', // shadow: 'open'
	}}
/>

<script module>
	export type Between0And1 = number & { __brand: 'Between0And1' };

	export type ReframeMarkerProps = {
		'layout-id'?: string;
		'trigger-position'?: Between0And1 | undefined;
		'root-margin'?: number | undefined;
		debug?: boolean;
		'trigger-type'?: 'enter' | 'leave' | undefined;
		'zoom-in'?: boolean;
		'zoom-out'?: boolean;
		'go-home'?: boolean;
		'go-to-target'?: string;
		'go-fullscreen'?: boolean;
		'trigger-direction'?: 'top' | 'bottom' | 'both';
	};

	export type ReframeMarker = HTMLElement & ReframeMarkerProps & { getMarkerOrder: () => number; getMarkerCallback: () => Object };
</script>

<script lang="ts">
	import { onMount } from 'svelte';
	import { v4 as uuidv4 } from 'uuid';
	import { get } from 'svelte/store';
	import cssVariables from '@src/utils/cssvariables';
	import { useTriggerObserver } from '@core/rf-observer/useTriggerObserver';
	import { addMarker, updateMarkerOrderId, RFMarkerOrderId } from '@src/store/markers.svelte';

	/**
	 * Props
	 */
	let {
		'trigger-position': triggerPosition,
		'root-margin': rootMargin,
		'layout-id': layoutId,
		debug,
		'trigger-type': triggerType = undefined,
		'zoom-in': zoomIn = false,
		'zoom-out': zoomOut = false,
		'go-home': goHome = false,
		'go-to-target': goToTarget = undefined,
		'go-fullscreen': goFullscreen = false,
		'trigger-direction': triggerDirection = 'both',
	}: ReframeMarkerProps = $props();

	/**
	 * Variables
	 */
	let markerOrder = 0;
	const componentId = uuidv4();

	/**
	 * Composables
	 */
	const { element, setTriggerCallback, getCallback } = useTriggerObserver({
		shouldObserve: !triggerPosition,
		debug,
		layoutId,
		rootMargin,
		triggerType,
		zoomIn,
		zoomOut,
		goHome,
		goToTarget,
		goFullscreen,
		triggerDirection,
	});

	/**
	 * Methods
	 */
	const checkTriggerPosition = (triggerPosition?: number | undefined): void => {
		if (triggerPosition === undefined) return;
		if (isNaN(triggerPosition) || triggerPosition < 0 || triggerPosition > 1) {
			console.warn(`${layoutId} : Invalid triggerPosition: ${triggerPosition}. should be between 0 and 1`);
		}
	};

	export const getMarkerOrder = (): number => {
		return markerOrder;
	};

	export const getMarkerCallback = (): (() => void) => {
		return getCallback() as () => void;
	};

	export const setCallback = (callbackFunction: () => void, type: 'enter' | 'leave' = 'enter'): void => {
		setTriggerCallback(callbackFunction, type);
	};

	/**
	 * Effects
	 */
	$effect(() => {
		checkTriggerPosition(triggerPosition);
	});

	/**
	 * Lifecycle
	 */
	onMount(() => {
		const currentElement = get(element);
		if (!currentElement) return;
		updateMarkerOrderId();
		addMarker(componentId + '-' + markerOrder.toString(), {
			...currentElement,
			getMarkerOrder: () => markerOrder,
			getMarkerCallback: () => getCallback() as Function,
		});
		markerOrder = get(RFMarkerOrderId);
	});
</script>

<span part="rf-marker" class="rf-marker" bind:this={$element} style={cssVariables({ '--debug-background': debug ? 'red' : '' })}>
	<slot></slot>
</span>

<style lang="scss" global>
	.rf-marker {
		background: var(--debug-background);
	}
</style>
